import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAsignaciones } from "../../../redux/actions";
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { TextField, InputAdornment, Button, IconButton, Paper } from '@mui/material';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
    FaHistory,
    FaSearch,
    FaFileExcel,
    FaLaptop,
    FaUser,
    FaExchangeAlt,
    FaCalendarAlt,
    FaSync
} from 'react-icons/fa';
import styles from "./HistoAsignacion.module.css";

const HistoAsignacion = () => {
    const dispatch = useDispatch();
    const asignaciones = useSelector((state) => state.asignaciones);
    const [search, setSearch] = useState("");

    useEffect(() => {
        dispatch(getAsignaciones());
    }, [dispatch]);

    const handleRefresh = () => {
        dispatch(getAsignaciones());
    };

    const handleExportExcel = () => {
        if (filteredData.length === 0) {
            Swal.fire({ icon: 'info', title: 'Sin datos', text: 'No hay registros para exportar.' });
            return;
        }

        const dataToExport = filteredData.map(item => ({
            'Código': item.codigo,
            'Usuario': item.usuario,
            'Tipo de Equipo': item.tipo,
            'Fecha Movimiento': item.fecha,
            'Observación': item.observacion
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Historial_Asignaciones");
        XLSX.writeFile(workbook, `Historial_Equipos_${new Date().toLocaleDateString()}.xlsx`);

        Swal.fire({
            icon: 'success',
            title: 'Reporte Generado',
            text: 'El archivo Excel ha sido descargado.',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const tableData = (asignaciones || []).map((item, index) => ({
        id: index,
        codigo: item.equipo?.cod || 'N/A',
        usuario: item.usuario?.nombre || 'N/A',
        tipo: item.tipoequipo?.descripcionte || 'N/A',
        fecha: item.asignacion?.fechaasignacion || 'N/A',
        observacion: item.asignacion?.observacion || 'N/A'
    })).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    const filteredData = tableData.filter(item =>
        item.usuario.toLowerCase().includes(search.toLowerCase()) ||
        item.codigo.toLowerCase().includes(search.toLowerCase())
    );

    const columns = [
        {
            field: 'codigo',
            headerName: 'Código',
            width: 130,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}>
                    <FaLaptop color="#3b82f6" /> {params.value}
                </div>
            )
        },
        {
            field: 'usuario',
            headerName: 'Responsable',
            width: 220,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaUser color="#64748b" /> {params.value}
                </div>
            )
        },
        { field: 'tipo', headerName: 'Tipo Equipo', width: 150 },
        {
            field: 'fecha',
            headerName: 'Fecha Movimiento',
            width: 180,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569' }}>
                    <FaCalendarAlt /> {params.value}
                </div>
            )
        },
        { field: 'observacion', headerName: 'Observación', width: 350 },
    ];

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h2 className={styles.title}>
                    <FaHistory /> Historial de Asignaciones
                </h2>
                <p className={styles.subtitle}>Trazabilidad completa de movimientos y cambios de responsabilidad de equipos.</p>
            </header>

            <div className={styles.toolbar}>
                <TextField
                    placeholder="Buscar por responsable o código de equipo..."
                    variant="outlined"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><FaSearch color="#3b82f6" /></InputAdornment>,
                        style: { borderRadius: '16px', background: '#f8fafc' }
                    }}
                />

                <IconButton
                    onClick={handleRefresh}
                    style={{ background: '#f1f5f9', color: '#1e293b' }}
                    title="Refrescar datos"
                >
                    <FaSync />
                </IconButton>

                <Button
                    variant="contained"
                    onClick={handleExportExcel}
                    startIcon={<FaFileExcel />}
                    style={{
                        background: '#10b981',
                        borderRadius: '16px',
                        padding: '10px 24px',
                        textTransform: 'none',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                        minWidth: '180px'
                    }}
                >
                    Exportar Excel
                </Button>
            </div>

            <div className={styles.gridWrapper} style={{ height: 600 }}>
                <DataGrid
                    rows={filteredData}
                    columns={columns}
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 15 } },
                    }}
                    pageSizeOptions={[15, 30, 50]}
                    disableSelectionOnClick
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f8fafc',
                            color: '#475569',
                            fontWeight: '800',
                            borderBottom: '2px solid #e2e8f0'
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#eff6ff',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                            color: '#1e293b'
                        }
                    }}
                />
                {!asignaciones || asignaciones.length === 0 ? (
                    <div className={styles.noData}>Cargando historial de movimientos...</div>
                ) : filteredData.length === 0 && (
                    <div className={styles.noData}>No se encontraron movimientos para su búsqueda.</div>
                )}
            </div>
        </div>
    );
};

export default HistoAsignacion;
